'use client';

import { faAngleDown, faMessageLines, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from '@pickleballinc/ui/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@pickleballinc/ui/components/ui/dialog';
import { Textarea } from '@pickleballinc/ui/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import clsx from 'clsx';
import { useParams } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';

import CheckboxForm from '@/components/atoms/CheckboxForm';
import { ActionButtons } from '@/components/molecules';
import { SendTextData, sendTextSchema } from '@/entities';
import { useSendMessageMutate } from '@/hooks/hooks';
import { useOrganizationStore } from '@/store/organization-store';

interface MessageablePlayer {
	playerUuid: string;
	fullname: string;
	sendTextAlerts: boolean;
}

interface TeamMessageModalProps {
	open: boolean;
	onClose: () => void;
	players: MessageablePlayer[];
	title: string;
	singlePlayerUuid?: string;
}

export const TeamMessageModal = ({ open, onClose, players, title, singlePlayerUuid }: TeamMessageModalProps) => {
	const store = useOrganizationStore();
	const params = useParams<{ teamId: string }>();
	const sendMessageMutation = useSendMessageMutate();

	const uniquePlayers = Array.from(new Map(players.map((player) => [player.playerUuid, player])).values());

	const form = useForm<SendTextData>({
		resolver: zodResolver(sendTextSchema),
		defaultValues: {
			message: '',
			players: uniquePlayers.reduce(
				(acc, p) => {
					acc[p.playerUuid] = singlePlayerUuid ? p.playerUuid === singlePlayerUuid : false;
					return acc;
				},
				{} as Record<string, boolean>
			)
		}
	});

	const { formState, register, handleSubmit } = form;
	const watchedPlayers = form.watch('players');
	const isPlayerSelected = Object.values(watchedPlayers).some(Boolean) || uniquePlayers.length === 0;

	const optedInPlayers = uniquePlayers.filter((player) => player.sendTextAlerts);
	const areAllOptedInSelected = optedInPlayers.length > 0 && optedInPlayers.every((player) => watchedPlayers[player.playerUuid]);

	const handleSelectAll = (checked: boolean) => {
		optedInPlayers.forEach((player) => {
			form.setValue(`players.${player.playerUuid}`, checked, { shouldDirty: true });
		});
	};

	const handleSubmitForm = (data: SendTextData) => {
		const users_uuid = Object.keys(data.players).filter((id) => data.players[id]);
		if (users_uuid.length === 0) return;

		sendMessageMutation.mutate(
			{
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				teamUuid: params.teamId,
				requestPayload: { users_uuid, message: data.message }
			},
			{
				onSuccess: () => {
					form.reset();
					onClose();
				}
			}
		);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) onClose();
			}}
		>
			<DialogContent className="max-w-[340px] rounded-lg sm:max-w-[524px] gap-0 p-6" hideClose>
				<DialogHeader className="flex w-full items-end justify-between flex-row">
					<DialogTitle>Send message to {title}</DialogTitle>
					<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500 cursor-pointer" onClick={onClose} />
				</DialogHeader>
				<FormProvider {...form}>
					<form onSubmit={handleSubmit(handleSubmitForm)}>
						{!singlePlayerUuid && optedInPlayers.length > 0 && (
							<div className="mt-4 flex items-center gap-2">
								<Checkbox
									className="size-5 rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
									id="select-all-players"
									checked={areAllOptedInSelected}
									onCheckedChange={(checked: boolean) => handleSelectAll(checked)}
								/>
								<label htmlFor="select-all-players" className="cursor-pointer text-base font-medium text-gray-500">
									Select all players (opted in to SMS)
								</label>
							</div>
						)}

						<hr className="my-4" />

						{!singlePlayerUuid && uniquePlayers.length > 0 && (
							<div className="scrollbar-thin mb-5 flex max-h-[170px] flex-col gap-3 overflow-auto">
								{uniquePlayers.map((player) => (
									<div className="flex" key={player.playerUuid}>
										{!player.sendTextAlerts ? (
											<TooltipProvider delayDuration={0} skipDelayDuration={0}>
												<Tooltip>
													<TooltipTrigger asChild>
														<div className="!text-gray-400 flex flex-row items-center gap-2">
															<CheckboxForm name={`players.${player.playerUuid}`} label={player.fullname} isDisabled />
															<FontAwesomeIcon className="text-error-500" icon={faMessageLines} size="sm" />
														</div>
													</TooltipTrigger>
													<TooltipPortal>
														<TooltipContent className="bg-gray-900">
															<p>This player has opted out of SMS messages.</p>
														</TooltipContent>
													</TooltipPortal>
												</Tooltip>
											</TooltipProvider>
										) : (
											<div className="flex flex-row items-center gap-2">
												<CheckboxForm name={`players.${player.playerUuid}`} label={player.fullname} />
												<FontAwesomeIcon className="text-green-500" icon={faMessageLines} size="sm" />
											</div>
										)}
									</div>
								))}
							</div>
						)}

						<Textarea
							{...register('message')}
							className={clsx('pt-4 focus-visible:ring-0', {
								'border-error-600 focus-visible:ring-1 focus-visible:ring-error-600': !!formState.errors.message
							})}
							placeholder="Your message..."
							rows={6}
						/>
						{!!formState.errors.message && <p className="text-sm text-error-600">{formState.errors.message?.message?.toString()}</p>}

						<details className="group my-6">
							<summary className="flex size-full cursor-pointer items-center justify-between rounded-t-lg px-2.5 py-3 text-base font-medium leading-5 text-[#EF6820] group-open:border group-open:border-solid group-open:border-gray-300">
								<div>Read text message restrictions</div>
								<FontAwesomeIcon icon={faAngleDown} className="ml-2 size-5 transition-transform duration-300 group-open:rotate-180" />
							</summary>
							<div className="overflow-hidden rounded-b-lg border-x border-b border-solid border-gray-300 px-3.5 py-2.5 text-sm text-gray-700 opacity-100 transition-all duration-300 ease-in-out">
								<div className="flex flex-col gap-4 text-sm leading-5 text-gray-900">
									<p>
										<strong>Absolutely no</strong> content pertaining to CBD, Alcohol, Marijuana, or any substance whatsoever.
									</p>
									<p>
										In addition, there is to be <strong>absolutely no</strong> advertising used with this text service.
									</p>
									<p>
										<strong>ALL</strong> messages are to be related to the league ONLY.
									</p>
									<p>
										<strong>ANY</strong> violation will be an immediate termination of your league.
									</p>
								</div>
							</div>
						</details>

						<ActionButtons
							className="w-full"
							submitButtonClassName="w-full text-white"
							closeButtonClassName="w-full"
							onClose={onClose}
							submitButtonText="Send"
							closeButtonText="Cancel"
							submitButtonDisabled={sendMessageMutation.isPending || !isPlayerSelected}
							submitTooltipMessage="Please select at least one player and enter a message."
						/>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};
